import { close } from "@/components/icons/SVGIcons";
import { Button } from "@/components/UI/Button";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import { Dispatch, SetStateAction } from "react";
import {
  returnFormattedDate,
  returnSizeFormatted,
  returnStringFormattedDateTime,
} from "../../../../lib/globalFormatterHelper";
import {
  applyColumnWidthMultiplier,
  applyDefaultMinWidths,
  formatIDToOnlyNumbers,
  onNodeCollapseChangeAllSettingsToFalse,
} from "./actionSummaryHelper";
import {
  eliminatedStatuses,
  quoteValueFormatter,
} from "./colDefs/colDefsShared";

import { IdefaultExpandedRowContext } from "@/components/widgets/tradeActionSummary/ActionSummaryTableHelperFunctions";
import QuoteCellRenderer from "@/components/widgets/tradeActionSummary/helper/cellRenderers/QuoteCellRenderer";
import { fillerColDef } from "@/lib/fillerColDef";
import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import { StructureTypeCode } from "@/lib/interfaces and Enums/ENUMStructureTypeCode";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import { updateQuotesAcrossTables } from "@/store/tradeDataStoreACTIONS";
import { toast } from "react-toastify";
import NotionalCellRenderer from "../../Creation/helper/cellRenderers/NotionalCellRenderer";
import RiskDV01CellRenderer from "../../Creation/helper/cellRenderers/RiskDV01CellRenderer";
import SelectPanelIndex from "../ActionSummaryOP/selectPanel/SelectPanelIndex";
import { returnColDefsAsNewArray } from "./actionSummaryColDefHelperFunctions";
import CouponSwapCellEditor from "./cellEditors/CouponSwapCellEditor";
import DynamicCellEditorQuoteDollarFraction from "./cellEditors/DynamicCellEditorQuoteDollarFraction";
import CouponSwapCellRenderer from "./cellRenderers/CouponSwapCellRenderer";
import IndicativeMidCellRenderer from "./cellRenderers/IndicativeMidCellRenderer";
import MainActionButtonActionSummary from "./cellRenderers/MainActionButtonActionSummary";
import MainDetailsCellRenderer from "./cellRenderers/MainTableActionSummaryESLevel/MainDetailsCellRenderer";
import MainESIDCellRenderer from "./cellRenderers/MainTableActionSummaryESLevel/MainESIDCellRenderer";
import ExpiresCellRenderer from "./cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer";
import { expiresCellValueFormatter } from "./cellRenderers/MainTableActionSummaryESLevel/ExpiresCellRenderer/expiresFormatter";
import TradeWideSettingsCellRenderer from "./cellRenderers/MainTableActionSummaryESLevel/TradeWideSettingsCellRenderer";
import TradeWideSettingsHeader from "./cellRenderers/MainTableActionSummaryESLevel/TradeWideSettingsCellRenderer/TradeWideSettingsHeader";
import { tradeWideSettingsValueFormatter } from "./cellRenderers/MainTableActionSummaryESLevel/TradeWideSettingsCellRenderer/tradeWideSettingsFormatter";
import featureFlagStore from "@/store/featureFlagStore";
import StructureTypeMainBlotterCellRenderer from "./cellRenderers/MainTableActionSummaryESLevel/StructureTypeMainBlotterCellRenderer";
import { structureTypeValueFormatter } from "./cellRenderers/MainTableActionSummaryESLevel/StructureTypeMainBlotterCellRenderer/structureTypeFormatter";
import ProviderCellRenderer from "./cellRenderers/ProviderCellRenderer";
import ReCouponAndNPVCheckboxCellRenderer from "./cellRenderers/ReCouponAndNPVCheckboxCellRenderer";
import StatusCellRenderer from "./cellRenderers/StatusCellRenderer";
import TransactionTypeCellRenderer from "./cellRenderers/TransactionTypeCellRenderer";
import { transactionTypeValueFormatter } from "./cellRenderers/TransactionTypeCellRenderer/transactionTypeFormatter";
import UnwindHeaderCheckbox from "./cellRenderers/UnwindHeaderCheckbox";
import ReferencePriceLegCellRenderer from "./cellRenderers/ReferencePriceLegCellRenderer";
import {
  CurrentCouponCellRenderer,
  QuotedMarginCellRenderer,
  ReferenceIndexCellRenderer,
} from "./cellRenderers/FRNBlockCellRenderers";
import {
  currentCouponValueFormatter,
  quotedMarginValueFormatter,
  referenceIndexValueFormatter,
} from "./cellRenderers/FRNBlockCellRenderers/frnBlockFormatter";
import ReferencePriceLegCellEditor from "./cellRenderers/ReferencePriceLegCellRenderer/ReferencePriceLegCellEditor";
import { referencePriceLegValueFormatter } from "./cellRenderers/ReferencePriceLegCellRenderer/referencePriceLegFormatter";
import { isReferencePriceCellEditable } from "./cellRenderers/ReferencePriceLegCellRenderer/referencePriceLegHelpers";
import {
  npvValueFormatter,
  recouponValueFormatter,
} from "./cellRenderers/unwindCheckboxFormatter";
import { returnTrueIfQuoteCellCanBeEdited } from "./columnHelpers/cellCanBeEdited";
import { ColId } from "./ENUMActionSummaryColIds";
import { IreturnColDefsForMainActionSummaryTable } from "./INTERFACESActionSummaryMainTableColDefs";
import { StatusRenderer } from "./statusCellStylingHelper";
import { findThisTradeSettings } from "../ActionSummaryOP/selectPanel/SelectPanelHelper";
import MainDetailsTooltip from "./tooltips/MainDetailsTooltip";
import MainStructureTooltip from "./tooltips/MainStructureTypeTooltip";
import {
  displayQuoteCellTooltip,
  displayYieldTooltipForAllQuoteLevels,
} from "./tooltips/tooltipHelperFunctions";
import { indicativeMidTooltipValueGetter } from "./tooltips/IndicativeMidTooltip";
import tradeDataStore from "@/store/tradeDataStore";
import CPTooltip from "./tooltips/CPTooltip";

export const isBidAskCellAtESEditable = (params: any): boolean => {
  if (params.data.role === "OP") return false;
  if (params.data.unwind === true) return false; // DON'T Allow editing NPV as they will be cash.
  if (params.data.type === "Order") return false; // Orders are edited via the fills.
  // console.log(params)
  return returnTrueIfQuoteCellCanBeEdited(params.data.status, params.colDef.colId, params);
};

export const getColours = (status: any) => {
  const waitForActionColour = ` bg-[#f7f288] `;

  const timeToActionColour = `bg-[#ff7863] `;

  const completeActionColour = ` bg-[#86efac] `;

  const noMoreActionsColour = ` bg-[#d1d5db] `;

  if (status == "New") {
    return timeToActionColour;
  } else if (status === "Sent") {
    return waitForActionColour;
  } else if (status === "Quoted") {
    return timeToActionColour;
  } else if (status === "Acknowledged") {
    return waitForActionColour;
  } else if (status === "Rejected") {
    return timeToActionColour;
  } else if (status === "Awarded") {
    return waitForActionColour;
  } else if (status === "Executed") {
    return timeToActionColour;
  } else if (status === "Affirmed" || status === "Complete") {
    return completeActionColour;
  } else {
    return noMoreActionsColour;
  }
};

export const getStatusForFiltering = Object.values(
  ExecutionStrategyStatus,
).sort();
export const getOutline = (status: any) => {
  const timeToActionColour = ` underline decoration-[#32b7f5] text-[#32b7f5] font-semibold `;

  if (status == "New") {
    return timeToActionColour;
  } else if (status === "Sent") {
    // return waitForActionColour;
  } else if (status === "Quoted") {
    return timeToActionColour;
  } else if (status === "Acknowledged") {
    // return waitForActionColour;
  } else if (status === "Rejected") {
    return timeToActionColour;
  } else if (status === "Awarded") {
    // return waitForActionColour;
  } else if (status === "Executed") {
    return timeToActionColour;
  } else if (status === "Affirmed" || status === "Complete") {
    // return completeActionColour;
  } else {
    // return closedActionColour;
  }
};

// API sends a comma-separated currency string (e.g. "EUR,USD") whose codes may
// carry stray whitespace ("EUR ,USD"). Split into clean codes, one per entry.
export const splitCurrencies = (raw: unknown): string[] =>
  typeof raw === "string"
    ? raw
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean)
    : [];

// "EUR, USD" for display, grouping and copy-paste.
export const formatCurrenciesValue = (raw: unknown): string =>
  splitCurrencies(raw).join(", ");

export const getBestCounterparty = (data: any, columnId?: string) => {
  if (data == undefined) {
    return;
  }
  if (data.type != "Not Sent") {
    if (data.role == "OP") {
      const counterparties = data.executionRequestCounterParties;

      let bestCounterparty: any = null;

      counterparties.forEach((cp: any) => {
        if (
          cp.status == ExecutionRequestStatus.Awarded ||
          cp.status == ExecutionRequestStatus.Executed ||
          cp.status == ExecutionRequestStatus.ExecutedPend ||
          cp.status == ExecutionRequestStatus.ExecutedRPChangeRequested ||
          cp.status == ExecutionRequestStatus.ExecutedPendRPChangeRequested ||
          cp.status == ExecutionRequestStatus.ExecutedRPAgreeChange ||
          cp.status == ExecutionRequestStatus.ExecutedPendRPAgreeChange ||
          cp.status == ExecutionRequestStatus.ExecutedOPChangeRequested ||
          cp.status == ExecutionRequestStatus.ExecutedPendOPChangeRequested ||
          cp.status == ExecutionRequestStatus.Affirmed ||
          cp.status == ExecutionRequestStatus.AffirmedPend ||
          // ORDERS
          cp.status == ExecutionRequestStatus.Filled ||
          cp.status == ExecutionRequestStatus.NoMoreFills
        ) {
          bestCounterparty = cp;
        } else if (cp.order == 1 && !eliminatedStatuses.includes(cp.status)) {
          bestCounterparty = cp;
        }
      });
      if (bestCounterparty) {
        return bestCounterparty;
      }
      // If only one CP remains in competition, show them
      const activeCounterparties = counterparties.filter(
        (cp: any) => !eliminatedStatuses.includes(cp.status),
      );
      if (activeCounterparties.length === 1) {
        return activeCounterparties[0];
      }
      // If only 1 counterparty, always show it - no ambiguity
      if (counterparties.length == 1) {
        return counterparties[0];
      }
      //if returning to the 'provider' column definition, send null
      return columnId == "ActionSummaryCounterpartyCell"
        ? null
        : counterparties[0]; // Catch all
    } else if (data.role == "RP") {
      return data;
    }
  }
};

// Shared function to get quote value (bid/ask/mid) for valueFormatter and valueGetter
export const getQuoteValue = (
  params: any,
  fieldName: "bid" | "ask" | "mid",
) => {
  if (params.data == undefined) {
    return "";
  }
  const bestCounterparty = getBestCounterparty(params.data);
  if (params.data.role == "RP") {
    if (
      params &&
      params.data &&
      params.data.legs &&
      params.data.legs.length > 0
    ) {
      return params.data.strategyType == "Outright"
        ? params.data.legs[0][fieldName]
        : params.data[fieldName];
    }
  }
  if (params.data.role == "OP" && bestCounterparty?.legs?.length > 0) {
    return params.data.strategyType == "Outright"
      ? bestCounterparty.legs[0][fieldName]
      : bestCounterparty[fieldName];
  } else return "";
};

// Helper function to get notation for ES table level (Main Action Summary Table)
export const getNotationForMainTable = (params: any, fullTradeData: any) => {
  const bestCounterparty = getBestCounterparty(params.data);
  const isOutright = fullTradeData?.strategyType == "Outright";

  if (bestCounterparty?.legs?.length > 0) {
    return isOutright
      ? bestCounterparty.legs[0].notation
      : fullTradeData.role == "OP"
        ? bestCounterparty.notation
        : bestCounterparty.notationER;
  }
  return undefined;
};

export function returnColDefsForMainActionSummaryTable({
  cryptoID,
  isMiniTable = false,
  gridRef,
}: IreturnColDefsForMainActionSummaryTable) {
  const colDefsForMainActionSummaryTable: (ColDef & Record<string, any>)[] = [
    {
      checkboxSelection: true,
      enableCellChangeFlash: false,
      width: defaultColumnWidths.checkbox + defaultColumnWidths.expandArrow,
      fixedWidth: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: "left", // This ensures the column stays in the first position
      suppressColumnsToolPanel: true,
      cellRenderer: "agGroupCellRenderer",
      headerClass: "ActionSummaryCheckboxHeader fixedWidthHeader",
      colId: "ActionSummaryCheckboxCell",
      headerCheckboxSelectionFilteredOnly: true,
      headerCheckboxSelection: true,
      hide: isMiniTable,
      mainMenuItems: ["resetColumns"],
    },
    {
      width: defaultColumnWidths.esid,
      field: "esid",
      headerName: TableHeaderField.esid,
      filter: "agTextColumnFilter",
      headerClass: "ActionSummaryTradeIDHeader",
      colId: "ActionSummaryTradeIDCell",
      lockVisible: true,
      // sort: "desc",
      enableRowGroup: false,
      cellRenderer: MainESIDCellRenderer,
      cellRendererParams: {
        cryptoID,
      },
      comparator: (valueA, valueB) => {
        // Extract the numeric part from "thisCompany-1234"
        const numA = formatIDToOnlyNumbers(valueA);
        const numB = formatIDToOnlyNumbers(valueB);

        return numA - numB;
      },
    },
    {
      field: "status",
      headerName: TableHeaderField.status,
      width: defaultColumnWidths.status,
      colId: ColId.ActionSummaryStatusCell,
      headerClass: "ActionSummaryStatusHeader",
      cellRenderer: StatusCellRenderer,
      enableRowGroup: true,
      lockVisible: true,
      filterParams: {
        values: getStatusForFiltering,
        comparator: () => {
          return 0;
        }, // stops the filter list from displaying in alphabetical order
      },
      cellStyle: (props) => {
        const baseStyles = StatusRenderer(props.data);
        return {
          ...baseStyles,
          borderRight: "Var(--ag-grid-cubeCustom-cell-border)",
        };
      },
      onCellClicked: (props: CellClickedEvent) => {
        const isExpanded = props.node.expanded;
        props.node.setExpanded(!isExpanded); // Toggle the expanded state
      },
    },
    {
      field: "status", //todo
      filter: false,
      headerName: "Action",
      lockVisible: true,
      colId: "ActionSummaryMainActionHeader",
      width: defaultColumnWidths.mainAction,
      minWidth: defaultColumnWidths.mainAction,
      // minWidth: 150,
      headerClass: "ActionSummaryActionHeader",
      suppressHeaderFilterButton: true,
      cellRenderer: MainActionButtonActionSummary,
      cellRendererParams: {
        cryptoID,
        gridRef,
      },
    },
    ...(featureFlagStore.getState().tradeSettingsSidebarAndCog
      ? [
          {
            // LQC-1301 — single settings icon: hover previews the trade-wide settings
            // (Auto Affirm / Cover Spread), click edits them in place past `Sent`.
            // OP-only + per-setting status gating are handled inside the renderer.
            field: "status",
            headerName: "Trade-wide settings",
            // LQC-1728 — cog icon as the heading (text would force the column wide)
            headerComponent: TradeWideSettingsHeader,
            headerTooltip: "Trade-wide settings",
            colId: "ActionSummaryTradeWideSettingsCell",
            width: 20,
            minWidth: 20,
            resizable: true,
            sortable: false,
            filter: false,
            suppressHeaderFilterButton: true,
            lockVisible: false,
            headerClass:
              "ActionSummaryTradeWideSettingsHeader shrinkableHeader",
            cellRenderer: TradeWideSettingsCellRenderer,
            valueFormatter: tradeWideSettingsValueFormatter,
            cellRendererParams: { cryptoID },
            hide: isMiniTable,
          },
        ]
      : []),
    {
      field: "bid", //todo
      filter: false,
      headerName: "Bid",
      headerClass: "ActionSummaryBidHeader",
      suppressHeaderFilterButton: true,
      tooltipValueGetter: displayQuoteCellTooltip,
      colId: "ActionSummaryBid",
      editable: isBidAskCellAtESEditable,
      cellEditorPopup: false,
      cellEditor: DynamicCellEditorQuoteDollarFraction,

      valueSetter: (params) => {
        if (params.data.role == "RP") {
          if (
            params.data?.strategyType == "Outright" &&
            params.data.legs?.length > 0
          ) {
            params.data.legs[0].bid = params.newValue;
            // params.data.legs[0].isupdated_bid = true;
          }
          params.data.bid = params.newValue;
          // params.data.isupdated_bid = true;
        }
        updateQuotesAcrossTables(params);
        return true;
      },
      valueGetter: (params) => getQuoteValue(params, "bid"),
      valueFormatter: quoteValueFormatter,
      cellClass: "ag-roundedInputStyle",
      width: defaultColumnWidths.quoteField,
      cellRendererParams: (params: any) => {
        return { fullTradeData: params.data, tableLevel: "ES" };
      },
      cellRenderer: (params: any) => {
        if (!params.data) {
          return null; //  Prevents breaking when grouped rows are rendered
        }
        return <QuoteCellRenderer {...params} />;
      },
    },
    {
      field: "ask", //todo
      filter: false,
      tooltipValueGetter: displayQuoteCellTooltip,
      suppressHeaderFilterButton: true,
      headerName: TableHeaderField.ask,
      colId: "ActionSummaryAsk",
      headerClass: "ActionSummaryAskHeader",
      editable: isBidAskCellAtESEditable,
      cellEditorPopup: false,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      valueSetter: (params) => {
        if (params.data.role == "RP") {
          if (
            params.data?.strategyType == "Outright" &&
            params.data.legs?.length > 0
          ) {
            params.data.legs[0].ask = params.newValue;
            // params.data.legs[0].isupdated_ask = true;
          }
          params.data.ask = params.newValue;
          // params.data.isupdated_ask = true;
        }
        updateQuotesAcrossTables(params);
        return true;
      },

      valueGetter: (params) => getQuoteValue(params, "ask"),
      valueFormatter: quoteValueFormatter,
      cellClass: "ag-roundedInputStyle",
      cellRendererParams: (params: any) => {
        return { fullTradeData: params.data, tableLevel: "ES" };
      },
      cellRenderer: (params: any) => {
        if (!params.data) {
          return null; //  Prevents breaking when grouped rows are rendered
        }
        return <QuoteCellRenderer {...params} />;
      },
      width: defaultColumnWidths.quoteField,
    },
    {
      field: "mid", //todo
      filter: false,
      tooltipValueGetter: displayYieldTooltipForAllQuoteLevels,
      suppressHeaderFilterButton: true,
      headerName: "Mid",
      headerClass: "ActionSummaryMidHeader",
      colId: "ActionSummaryMid",
      cellEditorPopup: false,
      cellEditor: DynamicCellEditorQuoteDollarFraction,
      valueSetter: (params) => {
        if (params.data.role == "RP") {
          if (
            params.data?.strategyType == "Outright" &&
            params.data.legs?.length > 0
          ) {
            params.data.legs[0].mid = params.newValue;
            // params.data.legs[0].isupdated_mid = true;
          }
          params.data.mid = params.newValue;
          // params.data.isupdated_mid = true;
          updateQuotesAcrossTables(params);
        }
        return true;
      },
      valueGetter: (params) => getQuoteValue(params, "mid"),
      valueFormatter: quoteValueFormatter,

      cellClass: "ag-roundedInputStyle",
      width: defaultColumnWidths.quoteField,
      cellRendererParams: (params: any) => {
        return { fullTradeData: params.data, tableLevel: "ES" };
      },
      cellRenderer: (params: any) => {
        if (!params.data) {
          return null; //  Prevents breaking when grouped rows are rendered
        }
        return <QuoteCellRenderer {...params} />;
      },
    },
    {
      field: "indicativeMid", //todo
      width: defaultColumnWidths.indicativeMid,
      headerName: TableHeaderField.indicativeMid,
      colId: "ActionSummaryIndicativeMid",
      headerClass: "ActionSummaryIndicativeMidHeader",
      filter: false,
      cellRendererParams: (params: any) => {
        return { fullTradeData: params.data };
      }, //tableLevel: "ES"
      cellRenderer: IndicativeMidCellRenderer,
      tooltipValueGetter: indicativeMidTooltipValueGetter,
    },
    {
      field: "provider",
      headerName: TableHeaderField.provider,
      width: defaultColumnWidths.provider,
      colId: "ActionSummaryCounterpartyCell",
      headerClass: ColId.ActionSummaryCounterpartyHeader,
      cellRenderer: ProviderCellRenderer,
      valueGetter: (params) => {
        // to get values for filtering
        if (params.data == undefined) {
          return;
        }
        const bestCounterparty = getBestCounterparty(
          params.data,
          params.colDef.colId,
        );
        if (params.data.role == "RP") {
          return params.data.originatingCounterparty;
        }
        if (params.data.role == "OP" && bestCounterparty) {
          return bestCounterparty.provider;
        } else return "";
      },
      enableRowGroup: true,
      tooltipValueGetter: (params) => {
        if (!params.data) return null;
        if (params.data.role === "OP") {
          return params.data.executionRequestCounterParties?.length > 0
            ? "cp"
            : null;
        }
        return (
          params.data.originatingCounterparty || params.data.provider || null
        );
      },
      tooltipComponent: CPTooltip,
    },
    {
      width: defaultColumnWidths.type, //todo
      field: "type",
      headerName: TableHeaderField.type,
      headerClass: "ActionSummaryTypeHeader",
      colId: "ActionSummaryTypeCell",
      enableRowGroup: true,
      filterParams: {
        values: ["RFQ", "Order", "Not Sent"],
      },
    },
    {
      field: "currencies",
      headerName: TableHeaderField.currency,
      width: defaultColumnWidths.currency,
      colId: "ActionSummaryCurrenciesCell",
      headerClass: "ActionSummaryCurrenciesHeader",
      hide: false,
      enableRowGroup: true,
      // LQC-3117 - an array gives one checkbox per currency, not one per "EUR, USD" combo.
      filter: "agSetColumnFilter",
      filterValueGetter: (params: any) =>
        splitCurrencies(params.data?.currencies),
      valueGetter: (params: any) =>
        formatCurrenciesValue(params.data?.currencies),
    },
    {
      field: "structureType",
      width: defaultColumnWidths.structureType, //todo
      enableRowGroup: true,
      headerName: TableHeaderField.structureType,
      headerClass: "ActionSummaryStructureHeader",
      colId: "ActionSummaryStructureCell",
      filter: "agSetColumnFilter",
      cellRenderer: StructureTypeMainBlotterCellRenderer,
      valueFormatter: structureTypeValueFormatter,
      tooltipField: "structureType",
      tooltipComponent: MainStructureTooltip,
    },
    {
      width: defaultColumnWidths.details, //todo
      field: "details",
      enableRowGroup: true,
      headerName: TableHeaderField.details,
      headerClass: "ActionSummaryDetailsHeader",
      colId: "ActionSummaryDetailsCell",
      cellDataType: "text",
      filter: "agTextColumnFilter",
      cellRenderer: MainDetailsCellRenderer,
      cellRendererParams: { cryptoID },
      // tooltipValueGetter: (params) => params.value,
      tooltipField: "details",
      tooltipComponent: MainDetailsTooltip,
    },
    {
      width: defaultColumnWidths.actionedInfo, //todo
      field: "actionedAt",
      headerName: TableHeaderField.actionedAt,
      headerClass: "ActionSummaryActionedAtHeader",
      colId: "ActionSummaryActionedAtCell",
      // enableRowGroup: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              buttons: ["reset"],
              comparator: (
                filterLocalDateAtMidnight: Date,
                cellValue: Date,
              ) => {
                if (!cellValue) return -1;

                const cellDate = new Date(cellValue);
                const cellYear = cellDate.getFullYear();
                const cellMonth = cellDate.getMonth(); // 0-based
                const cellDay = cellDate.getDate();

                const filterYear = filterLocalDateAtMidnight.getFullYear();
                const filterMonth = filterLocalDateAtMidnight.getMonth();
                const filterDay = filterLocalDateAtMidnight.getDate();

                if (cellYear < filterYear) return -1;
                if (cellYear > filterYear) return 1;

                if (cellMonth < filterMonth) return -1;
                if (cellMonth > filterMonth) return 1;

                if (cellDay < filterDay) return -1;
                if (cellDay > filterDay) return 1;
                return 0; // Exact match - T
              },
            },
          },
          {
            field: "date",
            // filter: DateWeekFilter,
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              suppressSelectAll: false,
              defaultToNothingSelected: true,
              suppressMiniFilter: true,
              convertValuesToStrings: true,
              values: (params: any) => {
                // Show both checkboxes
                // Virtual Dom columns are used as a safeguard against fast UI changed through the workspaces tabs.
                // If the virtual cols are undefined, the grid api is possilby old.
                const currentVirtualColumns =
                  gridRef.current?.api.getAllDisplayedVirtualColumns();
                if (currentVirtualColumns) {
                  params.success(["Today", "Week (Excluding Today)"]);
                }
              },
              keyCreator: (params: any) => {
                const raw = params.value;
                if (!raw || isNaN(new Date(raw).getTime())) return "-";

                const cellDate = new Date(raw);

                const today = new Date();
                const startOfToday = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate(),
                );

                const normalizedCellDate = new Date(
                  cellDate.getFullYear(),
                  cellDate.getMonth(),
                  cellDate.getDate(),
                );

                const isToday =
                  normalizedCellDate.getTime() === startOfToday.getTime();

                // Week range: Mon–Fri (including today)
                const day = startOfToday.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
                const daysFromMonday = day === 0 ? -6 : 1 - day;
                const startOfWeek = new Date(startOfToday);
                startOfWeek.setDate(startOfWeek.getDate() + daysFromMonday);

                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 4); // Friday

                const isWeek =
                  normalizedCellDate >= startOfWeek &&
                  normalizedCellDate <= endOfWeek;

                if (isToday) return "Today";
                if (isWeek) return "Week (Excluding Today)";

                return "-";
              },
              valueFormatter: (params: any) => params.value,
            },
          },
        ],
      },
      valueFormatter: (params: any): string => {
        if (!params.value || params == undefined) return ""; // empty string instead of undefined

        if (params.data) {
          if (params.value) {
            return returnStringFormattedDateTime(params.value);
          } else if (params.data.actionedAt) {
            return returnStringFormattedDateTime(params.data.actionedAt.value);
          }
        }
        return "";
      },
    },
    {
      width: defaultColumnWidths.latestActivity, //todo
      hide: true,
      field: "latestActivity",
      enableRowGroup: true,
      headerName: TableHeaderField.latestActivity,
      headerClass: "ActionSummaryLatestActivityHeader",
      colId: "ActionSummaryLatestActivityCell",
      filterParams: {
        values: [...getStatusForFiltering, "Unawarded"],
        comparator: () => {
          return 0;
        }, // stops the filter list from displaying in alphabetical order
      },
      cellRenderer: (params: any) => {
        if (
          params.data &&
          params.data.role == "OP" &&
          params.value == "Recouponed"
        ) {
          toast(
            `${params.data.counterparty} has recouponed  ${params.data.esid}`,
          );
          params.node.toastShown = true;
        }
        return params.value;
      },
    },
    ...(featureFlagStore.getState().tradeExpiryColumn
      ? [
          {
            // LQC-2171 - when the whole trade/RFQ stops being live, gated on
            // TradeExpiryColumnFeature. Read via getState() at build time, which
            // is safe because ActionSummaryIndex holds the grid back until
            // featureFlagsLoaded. The renderer carries the countdown (LQC-2173)
            // and the OP's edit pencil (LQC-2172), so the column is wider than a
            // plain date to fit both without ellipsis.
            // Starting width only - no minWidth, so the user can drag it as
            // narrow as any other column.
            width: defaultColumnWidths.expiry,
            field: "expiryEnd",
            headerName: TableHeaderField.expiryEnd,
            headerClass: "ActionSummaryExpiresHeader",
            colId: "ActionSummaryExpiresCell",
            enableRowGroup: true,
            hide: isMiniTable,
            cellRenderer: ExpiresCellRenderer,
            valueFormatter: expiresCellValueFormatter,
          },
        ]
      : []),
    {
      width: defaultColumnWidths.actionedInfo, //todo
      field: "actionedBy",
      hide: true,
      headerName: TableHeaderField.actionedBy,
      headerClass: "ActionSummaryActionedByHeader",
      colId: "ActionSummaryActionedByCell",
      enableRowGroup: true,
    },
    {
      width: defaultColumnWidths.actionedInfo, //todo
      field: "owner",
      hide: true,
      headerName: TableHeaderField.owner,
      headerClass: "ActionSummaryOwnerHeader",
      colId: "ActionSummaryOwnerCell",
      enableRowGroup: true,
    },
    {
      width: defaultColumnWidths.extStructureId, //todo
      field: "extStructureId",
      headerName: TableHeaderField.extStructureId,
      extraScaling: true,
      headerClass: "ActionSummaryExternalIDHeader",
      colId: "ActionSummaryExternalIDCell",
      filter: "agTextColumnFilter",
      hide: true,
      enableRowGroup: true,
    },

    ...fillerColDef,
  ];

  return applyDefaultMinWidths(colDefsForMainActionSummaryTable);
}

export interface IColDefsObject {
  theme: any;
  expandedRowsContextCurrent: IdefaultExpandedRowContext[];
  setExpandedRowsContext: Dispatch<
    SetStateAction<IdefaultExpandedRowContext[]>
  >;
  fullTradeData: any;
  getAccessTokenSilently: Function;
  setExpandedRowsContextMostRecentlyUpdated: React.Dispatch<
    React.SetStateAction<string>
  >;
  cryptoID?: string | undefined;
  isShowingInSelectPanel?: boolean;
  columnWidthMultiplier?: number;
  isMiniTable?: boolean;
}

export function returnColDefsForAllocations({
  expandedRowsContextCurrent,
  setExpandedRowsContext,
  fullTradeData,
  setExpandedRowsContextMostRecentlyUpdated,
}: IColDefsObject) {
  const colsToReturn = [
    {
      field: "security", //todo
      headerComponent: () => (
        <div className="flex items-center">
          <Button
            variant="exit"
            size="icon_sm"
            id="closeAllocationsBtn"
            onClick={() =>
              onNodeCollapseChangeAllSettingsToFalse(
                expandedRowsContextCurrent,
                setExpandedRowsContext,
                fullTradeData.esid,
                setExpandedRowsContextMostRecentlyUpdated,
              )
            }
          >
            {close}
          </Button>
          <div className="pl-2">{TableHeaderField.security}</div>
        </div>
      ),
      minWidth: defaultColumnWidths.name,
      // flex: 1,
      hide: false,
    },
    // LQC-227: ISIN column for bond trades only - Uncomment once API has ISIN included.
    // ...(tradeHasBondOrBondOption(fullTradeData)
    //   ? [
    //       {
    //         field: "isin",
    //         headerName: "ISIN",
    //         width: 150,
    //         hide: false,
    //       },
    //     ]
    //   : []),
    {
      field: "portfolio", //todo
      headerName: TableHeaderField.portfolio,
      width: defaultColumnWidths.portfolio,
      hide: false,
    },
    {
      field: "portfolioName", //todo
      headerName: TableHeaderField.portfolioName,
      width: defaultColumnWidths.portfolioName,
      hide: false,
    },
    {
      field: "side", //todo
      width: defaultColumnWidths.side,
      hide: false,
      headerName: TableHeaderField.direction,
      valueFormatter: (params: any) => {
        if (params && params.data && params.value) {
          // if (params.data.role == "RP") {
          return `${params.data?.originatingCounterparty || ""} ${params.value}`;
          // }
        }
      },
    },
    {
      field: "nominal", //todo
      width: defaultColumnWidths.nominal,
      hide: false,
      headerName: TableHeaderField.notionalLeg1,
      cellRenderer: NotionalCellRenderer,
    },
    {
      field: "complianceStatus", //todo
      extraScaling: true,
      width: defaultColumnWidths.complianceStatus,
      hide: false,
      headerName: TableHeaderField.complianceStatus,
    },
    {
      field: "settlementDate", //todo
      extraScaling: true,
      width: defaultColumnWidths.date,
      hide: false,
      headerName: TableHeaderField.settlementDate,
      valueFormatter: (params: any) => {
        if (params.value) {
          return returnFormattedDate(params.value);
        }
      },
    },
    {
      field: "extStructureId", //todo
      // width: 130,
      extraScaling: true,
      width: defaultColumnWidths.extStructureId,
      hide: false,
      headerName: TableHeaderField.extStructureId,
    },
    ...fillerColDef,
  ];

  // Remove cols from RP view only:
  // 14819 - Compliance status and External ID - PR.
  if (fullTradeData.role == "RP") {
    const colsForRRNotToSee = ["complianceStatus", "extStructureId"];
    const RPFilteredCols = colsToReturn.filter((col) => {
      if (col.field) {
        if (!colsForRRNotToSee.includes(col.field)) {
          return col;
        }
      } else {
        // Still return cols without a field to allow for things like filler cols.
        return col;
      }
    });
    return RPFilteredCols;
  }

  return colsToReturn;
}

export function returnColDefsForLegs({
  expandedRowsContextCurrent,
  setExpandedRowsContext,
  fullTradeData,
  setExpandedRowsContextMostRecentlyUpdated,
  isShowingInSelectPanel = false,
  columnWidthMultiplier = 1.0,
}: IColDefsObject) {
  const viewBlockColDefs = [
    {
      field: "securityName", //todo
      headerName: TableHeaderField.security,
      colId: ColId.viewBlockSecurityName, // "ViewBlockSecurityName",
      extraScaling: true, // Security name needs extra width at large fonts
      headerComponent: () => (
        <div className="flex">
          {isShowingInSelectPanel
            ? null
            : fullTradeData.status != ExecutionStrategyStatus.Cancelled && (
                <Button
                  variant="exit"
                  size="icon_sm"
                  id="closeLegsBtn"
                  onClick={() =>
                    onNodeCollapseChangeAllSettingsToFalse(
                      expandedRowsContextCurrent,
                      setExpandedRowsContext,
                      fullTradeData.esid,
                      setExpandedRowsContextMostRecentlyUpdated,
                    )
                  }
                >
                  {close}
                </Button>
              )}

          <div className={`${isShowingInSelectPanel ? "" : "pl-1"}`}>
            {TableHeaderField.securityName}
          </div>
        </div>
      ),
      minWidth: defaultColumnWidths.name,
      // flex: 1,
    },
    // LQC-227: ISIN column for bond trades — visibility controlled by colIdsToShow
    {
      field: "isin",
      headerName: "ISIN",
      colId: ColId.viewBlockIsin,
      width: 150,
    },
    // LQC-2153: FRN columns sit after Security - visibility controlled by colIdsToShow
    {
      field: "referenceIndex",
      headerName: TableHeaderField.referenceIndex,
      colId: ColId.viewBlockReferenceIndex,
      width: defaultColumnWidths.referenceIndex,
      cellRenderer: ReferenceIndexCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: referenceIndexValueFormatter,
    },
    {
      field: "quotedMargin",
      headerName: TableHeaderField.quotedMargin,
      colId: ColId.viewBlockQuotedMargin,
      width: defaultColumnWidths.quotedMargin,
      cellRenderer: QuotedMarginCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: quotedMarginValueFormatter,
    },
    {
      field: "coupon", // FRN Current Coupon reuses the block coupon
      headerName: TableHeaderField.currentCoupon,
      colId: ColId.viewBlockCurrentCoupon,
      width: defaultColumnWidths.currentCoupon,
      cellRenderer: CurrentCouponCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: currentCouponValueFormatter,
    },
    {
      field: "direction", //todo
      colId: ColId.viewBlockDirection,
      width: defaultColumnWidths.side,
      headerName: TableHeaderField.direction,
      valueFormatter: (params: any) => {
        const currentTradeData = tradeDataStore.getState().tradeData;
        const currentTrade = currentTradeData.find(
          (trade) => trade.esid === params.data.esid,
        );
        if (currentTrade) {
          return `${currentTrade.originatingCounterparty ?? ""} ${params.value}`.trim();
        }
        return params.value;
      },
    },

    {
      field: "notionalLeg1", //todo
      colId: ColId.ViewBlockNotionalLeg1,
      headerName: TableHeaderField.notionalLeg1,
      width: defaultColumnWidths.nominal,
      valueFormatter: (params: any) => {
        // TODO: Will not copy out the correct value.
        return params.value
          ? returnSizeFormatted(params.value, params.data?.instrumentType)
          : "";
      },
    },
    {
      field: "dV01", //todo
      colId: ColId.viewBlockdv01,
      headerName: TableHeaderField.dV01,
      width: defaultColumnWidths.dv01,
      cellDataType: "number",
      cellRenderer: RiskDV01CellRenderer,
    },
    {
      field: "effectiveDate", //todo
      colId: ColId.viewBlockEffectiveDate,
      headerName: TableHeaderField.effectiveDate,
      width: defaultColumnWidths.date,
      valueFormatter: (params: any) => {
        if (params.value) {
          return returnFormattedDate(params.value);
        }
      },
    },
    {
      field: "extStructureId", //todo
      colId: ColId.viewBlockExtStructureId,
      headerName: TableHeaderField.extStructureId,
      extraScaling: true, // External ID needs extra width at large fonts
      width: defaultColumnWidths.extStructureId,
    },
    {
      field: "transactionType",
      colId: ColId.viewBlockTransactionType,
      headerName: TableHeaderField.transactionType,
      width: defaultColumnWidths.transactionType,
      cellRenderer: TransactionTypeCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: transactionTypeValueFormatter,
    },
    {
      field: "npv",
      colId: ColId.viewBlockNPV,
      headerComponent: UnwindHeaderCheckbox,
      headerComponentParams: { fullTradeData: fullTradeData, fieldType: "npv" },
      width: defaultColumnWidths.checkbox + defaultColumnWidths.npv,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellDataType: "boolean",
      cellRenderer: ReCouponAndNPVCheckboxCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: npvValueFormatter,
    },
    {
      field: "recoupon",
      colId: ColId.viewBlockRecoupon,
      headerComponent: UnwindHeaderCheckbox,
      headerComponentParams: {
        fullTradeData: fullTradeData,
        fieldType: "recoupon",
      },
      width: defaultColumnWidths.checkbox + defaultColumnWidths.recoupon,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellDataType: "boolean",
      cellRenderer: ReCouponAndNPVCheckboxCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: recouponValueFormatter,
    },
    {
      field: "coupon", // Legs table.
      colId: ColId.viewBlockCoupon,
      headerName: TableHeaderField.coupon,
      width: defaultColumnWidths.coupon,
      editable: (params: any) => {
        const currentBlockData = tradeBlockDataStore.getState().tradeBlockData;
        const theseBlocks = currentBlockData.find(
          (blocks) =>
            blocks.esid == params.colDef.cellRendererParams.fullTradeData.esid,
        )?.blocks;
        const thisBlock = theseBlocks?.find(
          (block) => block.id == params.data.id,
        );
        const unwind = thisBlock?.unwind;
        if (unwind != null) {
          return true;
        } else return false;
      },
      cellRenderer: CouponSwapCellRenderer,
      cellEditor: CouponSwapCellEditor,
      cellRendererParams: { fullTradeData: fullTradeData },
    },
    {
      field: "indicativeMid",
      colId: ColId.viewBlockIndicativeMid,
      headerName: TableHeaderField.indicativeMid,
      width: defaultColumnWidths.indicativeMid,
      cellRenderer: IndicativeMidCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      tooltipValueGetter: indicativeMidTooltipValueGetter,
    },
    {
      field: "referencePrice", // Order
      //Map value from Set Price
      colId: ColId.viewBlockReferencePrice,
      headerName: TableHeaderField.referencePrice,
      width: defaultColumnWidths.referencePrice,
      cellRenderer: ReferencePriceLegCellRenderer,
      cellRendererParams: { fullTradeData: fullTradeData },
      valueFormatter: referencePriceLegValueFormatter,
      editable: isReferencePriceCellEditable,
      cellEditor: ReferencePriceLegCellEditor,
      cellClass: "ag-roundedInputStyle",
      hide: true, // Hidden by default, shown when Order
    },
    // {
    //   field: "referenceSpread",
    //   colId: ColId.viewBlockReferenceSpread,
    //   headerName: TableHeaderField.referenceSpread,
    //   width: defaultColumnWidths.referenceSpread,
    //   // cellRenderer: IndicativeNPVCellRenderer,
    //   cellRendererParams: { fullTradeData: fullTradeData },
    // },
    // {
    //   field: "referenceAsset",
    //   colId: ColId.viewBlockReferenceAsset,
    //   headerName: TableHeaderField.referenceAsset,
    //   width: defaultColumnWidths.referenceAsset,
    //   // cellRenderer: IndicativeNPVCellRenderer,
    //   cellRendererParams: { fullTradeData: fullTradeData },
    // },
    ...fillerColDef,
  ];

  const viewBlockColsUnwind: any[] = [
    ColId.viewBlockTransactionType,
    ColId.viewBlockNPV,
    ColId.viewBlockRecoupon,
    ColId.viewBlockCoupon,
    ColId.viewBlockIndicativeMid,
    ColId.viewBlockIndicativeNPV,
  ];

  const viewBlockColsOrder: any[] = [
    ColId.viewBlockReferencePrice,
    ColId.viewBlockReferenceSpread,
    ColId.viewBlockReferenceAsset,
  ];

  const colIdsToShow: any[] = [
    ColId.viewBlockSecurityName,
    ColId.viewBlockDirection,
    ColId.ViewBlockNotionalLeg1,
    ColId.viewBlockdv01,
    ColId.viewBlockEffectiveDate,
    ColId.viewBlockExtStructureId,
  ];

  // logic needed for showing / hiding order related cols.
  // A - use the full trade data so a trade past new will work.
  // B - For "New" status, check the settings store to determine if it's an Order
  const currentStatus = fullTradeData.status;
  let isOrder = false;

  if (currentStatus == ExecutionStrategyStatus.New) {
    // For New status, get the trade type from the settings store
    const thisTradeSettings = findThisTradeSettings({
      esid: fullTradeData.esid,
    });
    const activeTradeType = thisTradeSettings?.tradeTypes?.find(
      (t) => t.active,
    );
    isOrder = activeTradeType?.name === "Order";
  } else {
    // For trades past New status, use the type from fullTradeData
    isOrder = fullTradeData.type == "Order";
  }

  // Always include order columns, but hide them when not an Order
  colIdsToShow.push(...viewBlockColsOrder);

  let colDefsToReturn: ColDef | any[];
  let pushedSwapCondition = false;
  let pushedBNDCondition = false;
  let pushedFRNCondition = false;

  // Check structureType first (available immediately on first render)
  if (fullTradeData.structureType === StructureTypeCode.BNDO) {
    colIdsToShow.push(ColId.viewBlockIsin, ColId.viewBlockIndicativeMid);
    pushedBNDCondition = true;
  }

  // Fallback to blocks for structures (e.g., PACK with BND/BNDO legs)
  const allBlocks = tradeBlockDataStore.getState().tradeBlockData;
  const thisBlock = allBlocks.find(
    (block) => block.esid === fullTradeData.esid,
  );
  thisBlock?.blocks?.forEach((block: any) => {
    if (block.instrumentType == "SWP" && pushedSwapCondition == false) {
      colIdsToShow.push(...viewBlockColsUnwind);
      pushedSwapCondition = true;
    } else if (
      (block.instrumentType == "BND" || block.securityType == "BNDO") &&
      pushedBNDCondition == false
    ) {
      colIdsToShow.push(
        ColId.viewBlockIsin, // LQC-227: ISIN column for bond/BNDO trades
        ColId.viewBlockIndicativeMid, //14814 is confirmed at mids are getting returned via API.
      );
      pushedBNDCondition = true;
    } else if (block.instrumentType == "FRN" && pushedFRNCondition == false) {
      colIdsToShow.push(
        ColId.viewBlockReferenceIndex, // LQC-2153: FRN block columns
        ColId.viewBlockQuotedMargin,
        ColId.viewBlockCurrentCoupon,
      );
      pushedFRNCondition = true;
    }
  });

  const columnsToShowForNPVorRecoupon: ColId[] = [];
  const columnsToHideForNPVorRecoupon: ColId[] = [];
  colDefsToReturn = returnColDefsAsNewArray(
    viewBlockColDefs,
    colIdsToShow,
    columnsToShowForNPVorRecoupon,
    columnsToHideForNPVorRecoupon,
  );

  // TODO: Could update all cols to be hide True to start like trade cols. Show/hide order columns based on isOrder
  colDefsToReturn = colDefsToReturn.map((colDef: any) => {
    // LQC-784: Set Level column only visible for orders at New status
    if (colDef.colId === ColId.viewBlockReferencePrice) {
      return {
        ...colDef,
        hide: !isOrder || currentStatus !== ExecutionStrategyStatus.New,
      };
    }
    if (viewBlockColsOrder.includes(colDef.colId)) {
      return { ...colDef, hide: !isOrder };
    }
    return colDef;
  });

  return applyColumnWidthMultiplier(colDefsToReturn, columnWidthMultiplier);
}

export function returnColDefsForEditingPanel({
  expandedRowsContextCurrent,
  setExpandedRowsContext,
  fullTradeData,
  setExpandedRowsContextMostRecentlyUpdated,
  cryptoID,
}: IColDefsObject) {
  const colDefs = [
    {
      field: "status", //todo
      resizable: false,
      headerComponent: (params: any) => (
        <div className="flex items-center">
          <Button
            variant="exit"
            size="icon_sm"
            id="closePanelBtn"
            onClick={() =>
              onNodeCollapseChangeAllSettingsToFalse(
                expandedRowsContextCurrent,
                setExpandedRowsContext,
                fullTradeData.esid,
                setExpandedRowsContextMostRecentlyUpdated,
              )
            }
          >
            {close}
          </Button>
          <div className="pl-2">{params.displayName}</div>
        </div>
      ),
      hide: false,
      headerName: "Edit Panel",
      suppressCellFocus: true,
      suppressCellSelection: true,
      cellRenderer: SelectPanelIndex,
      // LQC-2860 - never put `data` in cellRendererParams. AG Grid deep-merges
      // these into params, and params.data IS the store's trade object, so a
      // snapshot here gets copied back over live fields on every refresh.
      cellRendererParams: {
        expandedRowsContext: expandedRowsContextCurrent,
        setExpandedRowsContext: setExpandedRowsContext,
        setExpandedRowsContextMostRecentlyUpdated:
          setExpandedRowsContextMostRecentlyUpdated,
        isEditPanel: true,
        cryptoID: cryptoID,
      },
      flex: 1,
      wrapText: true,
      autoHeight: true,
      // cellSelection: false,
      editable: false, // Prevents grid-specific cell editing
      cellClass: "EditPanelBackgroundStyle", // Add a class for styling
    },
  ];

  return colDefs;
}

export function returnColDefsForTradeAtNew({
  expandedRowsContextCurrent,
  setExpandedRowsContext,
  setExpandedRowsContextMostRecentlyUpdated,
  cryptoID,
  isMiniTable,
}: IColDefsObject) {
  const colDefs = [
    {
      field: "status", //todo
      hide: false,
      resizable: false,
      headerName: "Select Panel",
      cellRenderer: SelectPanelIndex,
      cellRendererParams: {
        expandedRowsContextCurrent,
        setExpandedRowsContext,
        setExpandedRowsContextMostRecentlyUpdated:
          setExpandedRowsContextMostRecentlyUpdated,
        cryptoID,
        isMiniTable,
      },
      flex: 1,
      wrapText: true,
      autoHeight: true,
      // cellSelection: false,
      cellClass: "EditPanelBackgroundStyle", // Add a class for styling
      // suppressCellFocus: true,
      // suppressRowClickSelection: true, // Stop the select panel from flickering when clicking in it.
    },
  ];

  return colDefs;
}
